'use client';

import { c as _c } from "react/compiler-runtime";
import { CalendarSolid, Clock } from '@byway/icons';
import { useLocale, useTranslations } from 'next-intl';
import { DialogTrigger } from 'react-aria-components';
import { Button } from '~/components/Button';
import { FlexibleDatesPanel } from '~/components/FlexibleDatesPanel';
import { Popover } from '~/components/Popover';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { usePathname, useRouter } from '~/i18n/navigation';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundContextParams, usePlaygroundDateType, usePlaygroundNights, usePlaygroundUIState } from '~/stores/playgroundStore';
import { formatExactDate } from '~/utils/dateHelpers/dateHelpers';
import styles from './DatesButton.module.css';
import { PRIMARY_EDIT_BAR_POPOVERS_MAX_WIDTH, PRIMARY_EDIT_BAR_POPOVERS_OFFSET } from './helpers';
interface DatesButtonProps {
  buttonVariant: 'pill' | 'tertiary';
}

/**
 * Desktop dates button with popover
 */
export function DatesButton(t0) {
  const $ = _c(35);
  const {
    buttonVariant
  } = t0;
  const pathname = usePathname();
  const isDesktop = useIsDesktop();
  const {
    isDrawerOpen,
    drawerItem
  } = usePlaygroundUIState();
  const {
    closeDrawer,
    openDrawer,
    setDatesTab
  } = usePlaygroundActions();
  const preferences = usePlaygroundContextParams();
  const playgroundContext = usePlaygroundContext();
  const nights = usePlaygroundNights();
  const dateType = usePlaygroundDateType();
  const router = useRouter();
  const t = useTranslations();
  const locale = useLocale();
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname?.includes("/review");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isReview = t1;
  const hasStartDate = typeof preferences?.startDate !== "undefined" && preferences?.startDate !== null;
  const isPopoverOpen = Boolean(isDesktop && isDrawerOpen && drawerItem === "dates");
  let t2;
  if ($[2] !== dateType || $[3] !== isReview || $[4] !== openDrawer || $[5] !== playgroundContext || $[6] !== preferences?.originId || $[7] !== router || $[8] !== setDatesTab) {
    t2 = async () => {
      if (!playgroundContext || !preferences?.originId) {
        return;
      }
      if (isReview) {
        router.back();
        openDrawer("dates");
        return;
      }
      setDatesTab(dateType === "nights" ? "nights" : "calendar");
      openDrawer("dates");
    };
    $[2] = dateType;
    $[3] = isReview;
    $[4] = openDrawer;
    $[5] = playgroundContext;
    $[6] = preferences?.originId;
    $[7] = router;
    $[8] = setDatesTab;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handlePress = t2;
  let t3;
  if ($[10] !== dateType || $[11] !== hasStartDate || $[12] !== locale || $[13] !== nights || $[14] !== preferences?.endDate || $[15] !== preferences?.startDate || $[16] !== t) {
    t3 = () => {
      if (!hasStartDate) {
        return <div className={styles.buttonContent}><CalendarSolid decorative={true} /><span>{t("journey.dates")}</span></div>;
      }
      if (dateType === "nights") {
        return <div className={styles.buttonContent}><Clock decorative={true} /><span>{nights ? t("journey.nights_count", {
              count: nights
            }) : t("journey.duration_label")}</span></div>;
      }
      return <div className={styles.buttonContent}><CalendarSolid decorative={true} /><span>{formatExactDate(preferences?.startDate, preferences?.endDate, locale)}</span></div>;
    };
    $[10] = dateType;
    $[11] = hasStartDate;
    $[12] = locale;
    $[13] = nights;
    $[14] = preferences?.endDate;
    $[15] = preferences?.startDate;
    $[16] = t;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const renderDatesContent = t3;
  let t4;
  if ($[18] !== closeDrawer) {
    t4 = isOpen => {
      if (!isOpen) {
        closeDrawer();
      }
    };
    $[18] = closeDrawer;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const handleOpenChange = t4;
  let t5;
  if ($[20] !== renderDatesContent) {
    t5 = renderDatesContent();
    $[20] = renderDatesContent;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== buttonVariant || $[23] !== handlePress || $[24] !== hasStartDate || $[25] !== t5) {
    t6 = <Button variant={buttonVariant} size="extraSmall" isPressed={hasStartDate} data-ua-click="playground-add-dates" dataTestId="playground-dates-button" onPress={handlePress}>{t5}</Button>;
    $[22] = buttonVariant;
    $[23] = handlePress;
    $[24] = hasStartDate;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FlexibleDatesPanel layout="horizontal" />;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== handleOpenChange) {
    t8 = <Popover ariaLabel="Edit travel dates" maxWidth={PRIMARY_EDIT_BAR_POPOVERS_MAX_WIDTH} offset={PRIMARY_EDIT_BAR_POPOVERS_OFFSET} placement="bottom" isDismissable={true} onOpenChange={handleOpenChange} close={true} noHeader={true}>{t7}</Popover>;
    $[28] = handleOpenChange;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== handleOpenChange || $[31] !== isPopoverOpen || $[32] !== t6 || $[33] !== t8) {
    t9 = <DialogTrigger isOpen={isPopoverOpen} onOpenChange={handleOpenChange}>{t6}{t8}</DialogTrigger>;
    $[30] = handleOpenChange;
    $[31] = isPopoverOpen;
    $[32] = t6;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
}

/**
 * Mobile dates button (drawer trigger)
 */
export function DatesButtonMobile(t0) {
  const $ = _c(25);
  const {
    buttonVariant
  } = t0;
  const pathname = usePathname();
  const {
    openDrawer,
    setDatesTab
  } = usePlaygroundActions();
  const preferences = usePlaygroundContextParams();
  const playgroundContext = usePlaygroundContext();
  const nights = usePlaygroundNights();
  const dateType = usePlaygroundDateType();
  const router = useRouter();
  const t = useTranslations();
  const locale = useLocale();
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname?.includes("/review");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isReview = t1;
  const hasStartDate = typeof preferences?.startDate !== "undefined" && preferences?.startDate !== null;
  let t2;
  if ($[2] !== dateType || $[3] !== isReview || $[4] !== openDrawer || $[5] !== playgroundContext || $[6] !== preferences?.originId || $[7] !== router || $[8] !== setDatesTab) {
    t2 = async () => {
      if (!playgroundContext || !preferences?.originId) {
        return;
      }
      if (isReview) {
        router.back();
        openDrawer("dates");
        return;
      }
      setDatesTab(dateType === "nights" ? "nights" : "calendar");
      openDrawer("dates");
    };
    $[2] = dateType;
    $[3] = isReview;
    $[4] = openDrawer;
    $[5] = playgroundContext;
    $[6] = preferences?.originId;
    $[7] = router;
    $[8] = setDatesTab;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handlePress = t2;
  let t3;
  if ($[10] !== dateType || $[11] !== hasStartDate || $[12] !== locale || $[13] !== nights || $[14] !== preferences?.endDate || $[15] !== preferences?.startDate || $[16] !== t) {
    t3 = () => {
      if (!hasStartDate) {
        return <div className={styles.buttonContent}><CalendarSolid decorative={true} /><span>{t("journey.dates")}</span></div>;
      }
      if (dateType === "nights") {
        return <div className={styles.buttonContent}><Clock decorative={true} /><span>{nights ? t("journey.nights_count", {
              count: nights
            }) : t("journey.duration_label")}</span></div>;
      }
      return <div className={styles.buttonContent}><CalendarSolid decorative={true} /><span>{formatExactDate(preferences?.startDate, preferences?.endDate, locale)}</span></div>;
    };
    $[10] = dateType;
    $[11] = hasStartDate;
    $[12] = locale;
    $[13] = nights;
    $[14] = preferences?.endDate;
    $[15] = preferences?.startDate;
    $[16] = t;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const renderDatesContent = t3;
  let t4;
  if ($[18] !== renderDatesContent) {
    t4 = renderDatesContent();
    $[18] = renderDatesContent;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== buttonVariant || $[21] !== handlePress || $[22] !== hasStartDate || $[23] !== t4) {
    t5 = <Button onPress={handlePress} variant={buttonVariant} size="extraSmall" isPressed={hasStartDate} dataTestId="playground-dates-button">{t4}</Button>;
    $[20] = buttonVariant;
    $[21] = handlePress;
    $[22] = hasStartDate;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
}